/**
 * Offline-first authentication.
 * - After a successful ONLINE password login we store a PBKDF2 verifier of the
 *   password (never the password itself) plus the session token + user object
 *   in the encrypted secureStore (AES-256-GCM over IndexedDB → device-bound).
 * - Quick PIN verifiers are stored the same way when the PIN is created.
 * - While offline, login is verified locally against these verifiers with the
 *   same 5-attempts / 15-minute lockout policy as the server.
 * - Every offline login is queued and auto-synced to
 *   /api/auth/sync-offline-logins when internet returns.
 */
import { secureGet, secureSet, secureDel } from "@/lib/secureStore";
import { api, getToken } from "@/lib/api";

const CRED_KEY = (m) => `am_offauth_${m}`;
const PROFILES_KEY = "am_offauth_profiles";
const EVENTS_KEY = "am_offauth_events";
const LOCK_KEY = (kind, m) => `am_offauth_lock_${kind}_${m}`;

const MAX_FAILS = 5;
const LOCK_MS = 15 * 60 * 1000;
const PBKDF2_ITERS = 60000;

const b64 = (buf) => btoa(String.fromCharCode(...new Uint8Array(buf)));

async function deriveVerifier(secret, saltB64) {
  const salt = saltB64
    ? Uint8Array.from(atob(saltB64), (c) => c.charCodeAt(0))
    : crypto.getRandomValues(new Uint8Array(16));
  const keyMaterial = await crypto.subtle.importKey(
    "raw", new TextEncoder().encode(secret), "PBKDF2", false, ["deriveBits"]
  );
  const bits = await crypto.subtle.deriveBits(
    { name: "PBKDF2", salt, iterations: PBKDF2_ITERS, hash: "SHA-256" },
    keyMaterial, 256
  );
  return { salt: b64(salt), hash: b64(bits) };
}

async function upsertProfile(mobile, patch) {
  const profiles = (await secureGet(PROFILES_KEY)) || [];
  const i = profiles.findIndex((p) => p.mobile === mobile);
  const next = { ...(i >= 0 ? profiles[i] : { mobile }), ...patch, updated_at: Date.now() };
  if (i >= 0) profiles[i] = next; else profiles.push(next);
  profiles.sort((a, b) => (b.updated_at || 0) - (a.updated_at || 0));
  await secureSet(PROFILES_KEY, profiles);
}

/** Profiles that can sign in offline on this device (newest first). */
export async function getOfflineProfiles() {
  return (await secureGet(PROFILES_KEY)) || [];
}

/** Persist encrypted password verifier + session after an ONLINE password login. */
export async function saveOfflineLogin(mobile, password, token, user) {
  const rec = (await secureGet(CRED_KEY(mobile))) || {};
  rec.pw = await deriveVerifier(password);
  rec.token = token;
  rec.user = user;
  await secureSet(CRED_KEY(mobile), rec);
  await upsertProfile(mobile, { name: user?.name || "", role: user?.role || "", hasPin: !!rec.pin, pinLength: rec.pinLength || 0 });
}

/** Persist encrypted PIN verifier when the user creates a Quick PIN. */
export async function saveOfflinePin(mobile, pin) {
  const rec = (await secureGet(CRED_KEY(mobile))) || {};
  rec.pin = await deriveVerifier(pin);
  rec.pinLength = pin.length;
  await secureSet(CRED_KEY(mobile), rec);
  await upsertProfile(mobile, { hasPin: true, pinLength: pin.length });
}

export async function clearOfflinePin(mobile) {
  const rec = await secureGet(CRED_KEY(mobile));
  if (rec) {
    delete rec.pin; delete rec.pinLength;
    await secureSet(CRED_KEY(mobile), rec);
  }
  await upsertProfile(mobile, { hasPin: false, pinLength: 0 });
}

async function lockState(kind, mobile) {
  const l = (await secureGet(LOCK_KEY(kind, mobile))) || { fails: 0, first: 0, until: 0 };
  if (l.until && l.until > Date.now()) {
    return { locked: true, minutes: Math.ceil((l.until - Date.now()) / 60000), rec: l };
  }
  return { locked: false, rec: l };
}

async function lockFail(kind, mobile, rec) {
  const now = Date.now();
  const windowOk = rec.first && rec.first + LOCK_MS > now;
  const fails = (windowOk ? rec.fails : 0) + 1;
  const next = { fails, first: windowOk ? rec.first : now, until: fails >= MAX_FAILS ? now + LOCK_MS : 0 };
  await secureSet(LOCK_KEY(kind, mobile), next);
  return MAX_FAILS - fails;
}

/**
 * Verify password or PIN locally while offline.
 * kind: "password" | "pin". Throws Error with coded message:
 *   locked:{min} | invalid:{remaining} | not_available
 * Returns { token, user } on success.
 */
export async function offlineLogin(mobile, secret, kind) {
  const rec = await secureGet(CRED_KEY(mobile));
  const verifier = kind === "pin" ? rec?.pin : rec?.pw;
  if (!rec || !verifier || !rec.token || !rec.user) throw new Error("not_available");

  const { locked, minutes, rec: lockRec } = await lockState(kind, mobile);
  if (locked) throw new Error(`locked:${minutes}`);

  const { hash } = await deriveVerifier(secret, verifier.salt);
  if (hash !== verifier.hash) {
    const remaining = await lockFail(kind, mobile, lockRec);
    await queueLoginEvent(kind === "pin" ? "offline_pin" : "offline_password", false);
    if (remaining <= 0) throw new Error(`locked:15`);
    throw new Error(`invalid:${remaining}`);
  }
  await secureDel(LOCK_KEY(kind, mobile));
  await queueLoginEvent(kind === "pin" ? "offline_pin" : "offline_password", true);
  return { token: rec.token, user: rec.user };
}

/** Queue an offline login event for later server-side history sync. */
export async function queueLoginEvent(method, success) {
  const events = (await secureGet(EVENTS_KEY)) || [];
  events.push({ method, success, client_ts: new Date().toISOString() });
  await secureSet(EVENTS_KEY, events.slice(-100));
}

/** Push queued offline login events to the backend (requires valid session). */
export async function syncOfflineLogins() {
  if (!navigator.onLine || !getToken()) return;
  const events = (await secureGet(EVENTS_KEY)) || [];
  if (!events.length) return;
  try {
    await api.post("/auth/sync-offline-logins", { events });
    await secureDel(EVENTS_KEY);
  } catch (e) { console.debug("offline login sync failed", e); }
}

// Auto-sync whenever internet returns.
if (typeof window !== "undefined") {
  window.addEventListener("online", () => { syncOfflineLogins(); });
}
